/**
 * Lecture data types based on S3 JSON schemas
 */

export interface LectureData {
  meta: {
    generated_at: string;
    version: string;
    tenant: {
      url_slug: string;
    };
    isTrialUser?: boolean; // 試用ユーザーの判定。true: 試用ユーザー, false: 一般ユーザー。本来はAPIから取得するが現在判定できないので一時的にjsonで判断。
  };
  /** 講義の数値ID。*/
  lecture_id?: number;
  /** 講義の内部ID（文字列) */
  internal_id?: string;
  course?: {
    /** 講座ID*/
    id: number;
    /** SNS共有URL等で使用する講座識別子 */
    data_type: string | null;
    /** 講座名 */
    name: string | null;
    /** 対象年度・月の組み合わせ */
    year_months: number[];
  };
  name: string;
  description: string;
  survey_url: string | null;
  is_rehearsaled: boolean;
  speakers: Speaker[];
  materials: Material[];
  message: LectureMessage | null;
  live: LiveConfig;
  archive: ArchiveConfig;
  question: {
    count: number;
  };
  chapters: Chapter[];
}

export interface Speaker {
  id: number;
  name: string;
  role: string | null;
  profile: string | null;
  image_url: string | null;
  sort_key: number;
}

export interface Material {
  id: number;
  filename: string;
  original_filename: string;
  description: string | null;
  file_size: number;
  mime_type: string;
  download_full_url: string;
  distribution_type: 0 | 1; // 0: 講義前, 1: 講義後
  uploaded_at: string;
}

/** 資料の配布タイミング表示ラベル */
export function getMaterialDistributionLabel(distributionType: Material['distribution_type']): string {
  return distributionType === 1 ? '講義後資料' : '講義前資料';
}

export interface LiveConfig {
  m3u8_url: string;
  started_at: string;
  ended_at: string;
  is_live_published: boolean;
  status: 'ready' | 'running' | 'ended';
}

/**
 * EQプレイヤー直接生成用パラメータ（管理バックエンドのJSONデプロイが出力）
 *
 * 埋め込みコード（embed_script）を介さずにプレイヤーを初期化するための3値。
 */
export interface EqPlayerParams {
  /** EQのディレクトリ名（プレイヤーJSのURL・bパラメータの組み立てに使用） */
  base_path: string;
  /** 契約会社IDのBase64エンコード値（cパラメータ） */
  contract_id: string;
  /** 動画IDのBase64エンコード値（mパラメータ。ReadAPIの obfuscated_mid と同値） */
  video_id: string;
}

export interface ArchiveConfig {
  actual_started_at: string;
  actual_ended_at: string;
  eq_setting_id: string | null;
  /** 2: EQ貼り付けタグ。eq_player がないレガシーJSONのフォールバックとして使用 */
  embed_script: string | null;
  /** 1: EQプレイヤー直接生成用パラメータ */
  eq_player?: EqPlayerParams | null;
  duration: number | null;
  is_archive_published: boolean;
}

export interface Chapter {
  id: number | string;
  title: string;
  start_time: number; // 秒（開始時間）
  duration?: number; // 秒（オプション）
  description?: string | null; // 説明（オプション）
  sort_key?: number; // 並び順（オプション、start_time でソートする場合は不要）
}

export interface LectureMessage {
  id: number;
  title: string;
  contents: string; // リッチテキスト（HTML）
  is_active: boolean;
}

export interface NoticeData {
  meta: {
    generated_at: string;
    version: string;
    tenant: {
      url_slug: string;
    };
  };
  notice: string;
}
